import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Sparkles, Link2, PencilRuler, ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function CreateProperty() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");

  useEffect(() => { track("create_property_started"); }, []);

  const startAI = async () => {
    setBusy("ai");
    track("ai_wizard_selected");
    try {
      const { data } = await api.post("/properties/wizard/start", { source: "ai" });
      navigate(`/dashboard/properties/${data.property.id}/wizard`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const createManual = async () => {
    setBusy("manual");
    track("manual_selected");
    try {
      const { data } = await api.post("/properties", { name: "Nuova struttura" });
      navigate(`/dashboard/properties/${data.property.id}/edit`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const goImport = () => { track("import_selected"); navigate("/dashboard/properties/import"); };

  return (
    <div className="px-4 pt-6 pb-10 max-w-md mx-auto">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> Indietro</button>
      <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Come vuoi iniziare?</h1>
      <p className="mt-1.5 text-slate-500">Scegli il modo più semplice per creare la tua guida. Potrai modificare tutto in qualsiasi momento.</p>

      <div className="mt-6 space-y-4">
        {/* Option 1 — Crea con l'AI (recommended) */}
        <button onClick={startAI} disabled={!!busy} data-testid="option-ai"
          className="relative w-full text-left rounded-3xl border-2 border-blue-500 bg-blue-50/60 p-6 shadow-sm shadow-blue-100 active:scale-[0.99] transition-transform disabled:opacity-60">
          <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide bg-blue-600 text-white px-2.5 py-1 rounded-full"><Check className="w-3 h-3" /> Consigliato</span>
          <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white grid place-items-center"><Sparkles className="w-6 h-6" /></div>
          <h2 className="mt-4 font-display font-bold text-xl text-slate-900">Crea con l'AI</h2>
          <p className="mt-1.5 text-slate-600">Rispondi a poche domande e lascia che l'AI prepari la tua Guest Guide.</p>
          <p className="mt-1 text-xs text-blue-600 font-medium">Il modo più semplice per iniziare.</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-white bg-blue-600 rounded-full px-4 py-2">{busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Inizia con l'AI <ArrowRight className="w-4 h-4" /></>}</span>
        </button>

        {/* Option 2 — Importa da annuncio */}
        <button onClick={goImport} disabled={!!busy} data-testid="option-import"
          className="w-full text-left rounded-3xl border border-slate-100 bg-white p-6 shadow-sm active:scale-[0.99] transition-transform disabled:opacity-60">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white grid place-items-center"><Link2 className="w-6 h-6" /></div>
          <h2 className="mt-4 font-display font-bold text-xl text-slate-900">Importa da annuncio</h2>
          <p className="mt-1.5 text-slate-600">Parti dalle informazioni che hai già. Inserisci il link del tuo annuncio e recupera automaticamente i dati disponibili.</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">Importa annuncio <ArrowRight className="w-4 h-4" /></span>
        </button>

        {/* Option 3 — Crea manualmente */}
        <button onClick={createManual} disabled={!!busy} data-testid="option-manual"
          className="w-full text-left rounded-3xl border border-slate-100 bg-white p-6 shadow-sm active:scale-[0.99] transition-transform disabled:opacity-60">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-700 grid place-items-center"><PencilRuler className="w-6 h-6" /></div>
          <h2 className="mt-4 font-display font-bold text-xl text-slate-900">Crea manualmente</h2>
          <p className="mt-1.5 text-slate-600">Inserisci personalmente tutte le informazioni e personalizza ogni dettaglio della tua struttura.</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">{busy === "manual" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Crea manualmente <ArrowRight className="w-4 h-4" /></>}</span>
        </button>
      </div>
    </div>
  );
}
